import { store, store$ } from '@redux/store';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  tap,
  distinctUntilKeyChanged,
  fromEvent
} from 'rxjs';
import {
  createHoveredMarker,
  paintMarker,
  removeHoveredMarker,
  removeMarkers,
  updateMarkerContent,
  updateMarkersVisibility
} from 'libraries/mapLibrary';
import uiStateSlice from 'layout/redux/uiStateSlice';
import {
  LocationType,
  ResultType,
  searchParametersActions,
  selectResultType
} from 'features/searchResultsFacets';
import { selectIsMarkerAutoScrollFeatureEnabled, selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
  isFetchingResults,
  areMarkersAvailable,
  areMapBoundariesDefined,
  geoCoordinates,
  mapMarkers,
  selectGeoCoordinatesAndLocation,
  selectedMarker,
  selectedNearbyListingCarousel,
  selectFocusedListingAndSitePlans,
  selectSitePlans
} from '../redux/selectors';
import { mapStateSlice } from '../redux/slices';
import {
  factsSearchParameters,
  featuresSearchParameters,
  locationSearchParameters,
  productSearchParameters,
  isNotCommunityLocationType
} from '../constants/searchParametersConstants';
import {
  mapContainerClicked$,
  mapContainerDoubleClicked$
} from '../redux/observables';
import { isUserInteractingWithSitePlan } from '../services/sitePlan';
import getClassesToApply from '../services/unitMarkerService';
import MarkerType from '../enums/markerType';
import { selectedMarkerAndPage } from '..';

const { actions } = mapStateSlice;
const mapContainer = document.querySelector('#nhs_map');

/**
 * It takes a marker object as an argument, and dispatches an action to the Redux store that sets the
 * selectedMarker property of the state to the marker object
 * @param marker - The marker object that was clicked on.
 */
const setActiveMarker = marker => {
  if (!marker) {
    store.dispatch(actions.selectedMarker(marker));
    return;
  }

  /*
   * The selectedMarker in state needs the page number,
   * then before to dispach the selectedMarker is necesary set it the page number
   * from the mapMarker state.
   */
  const newSelectedMarker = Object.assign({}, marker);
  const state = store.getState();
  const { pageNumber, isFeaturedListing } = mapMarkers(state).find(
    housing => housing.id === newSelectedMarker.id
  );
  newSelectedMarker.pageNumber = pageNumber;
  newSelectedMarker.isFeaturedListing = !!isFeaturedListing;
  store.dispatch(actions.selectedMarker(newSelectedMarker));

  document.body.dispatchEvent(
    new CustomEvent('markerPinCLicked', {
      detail: {
        marker: newSelectedMarker
      }
    })
  );
};

/**
 * It takes a marker object as an argument, and dispatches an action to the Redux store that sets the
 * selectedMarker property of the state to the marker object
 * @param marker - The marker object that was clicked on.
 */
const setActiveNearbyListingsCarousel = listings =>
  store.dispatch(actions.setActiveNearbyListingsCarousel(listings));

/**
 * Retrieves the DOM node for a marker icon based on the provided marker's id or communityId.
 *
 * - Searches for an element with [data-marker-icon] and a
 * - matching data-id attribute (first by id, then by communityId).
 * - Returns the marker DOM node if found, or null if not found.
 *
 * @param {number|string} param0.id - The unique id of the marker.
 * @param {number|string} param0.communityId - The community id of the marker.
 * @returns {Element|null} The marker DOM node if found, otherwise null.
 */
const getMarkerNode = ({ id, communityId }) => {
  const marker =
    document.querySelector(`[data-marker-icon][data-id="${id}"]`) ||
    document.querySelector(`[data-marker-icon][data-id="${communityId}"]`);

  return marker;
};

/* Dispatching an action to the store to fetch the markers. */
const fetchResults = () => {
  store.dispatch(
    actions.fetchResults({
      isFetchingResults: true,
      parameters: store.getState().searchParameters
    })
  );
};

/**
 * Removing active tooltip class and the hovered.
 */
const removeMarkerHovered = () => {
  const markerIcon = document.querySelector(
    `[data-marker-icon][data-hovered="true"]`
  );
  if (!markerIcon) {
    return;
  }
  markerIcon
    .closest('[data-geographic-key]')
    .removeAttribute('data-marker-hovered');
  markerIcon.classList.remove('nhs-mq-marker-hovered--large');
  delete markerIcon.dataset.hovered;

  const markerElement = markerIcon?.closest('[data-marker-element]');
  markerElement?.classList.remove('marker--active');
};

/**
 * Removing the the tooltip when the summary card link is clicked.
 */
fromEvent(document, 'NearbyListingsCardLinkClick').subscribe(
  removeMarkerHovered
);

/**
 * Removing the active class to marker to close the marker card.
 */
const closeMarkerCard = () => {
  const markerIcon = document.querySelector(
    `[data-marker-icon][data-selected]`
  );
  if (!markerIcon) {
    return;
  }

  const markerElement = markerIcon.closest('[data-marker-element]');
  removeMarkerHovered();
  const { selected } = getClassesToApply({
    isAdult: markerIcon.dataset.isAdult === 'true',
    isMasterPlannedCommunity:
      markerIcon.dataset.isMasterPlannedCommunity === 'true',
    isSpotlight: markerIcon.dataset.isSpotlight === 'true',
    isSelected: true
  });
  selected.forEach(className => markerIcon.classList.remove(className));
  delete markerIcon.dataset.selected;
  markerElement?.classList.remove('active');
  markerElement?.classList?.remove('marker--active');
};

/**
 * A function that check if the marker is the active marker. 
 * */
const isMarkerActive = marker => {
  const mapState = store?.getState()?.mapState;
  return marker.id === mapState?.selectedMarker?.id;
};

/**
 * A function that handler markers according to the user interaction of the map.
 */
const handleMarkerMapChanges = () => {
  const state = store.getState();
  const isMarkerSelected = !!state.mapState.selectedMarker;
  const carousel = selectedNearbyListingCarousel(state);

  if (isMarkerSelected) {
    setActiveMarker(null);
  }

  if (carousel) {
    setActiveNearbyListingsCarousel(null);
  }

  closeMarkerCard();
};

/**
 * Add the active class to markert to open the marker card.
 */
const openMarkerCard = marker => {
  marker
    .closest('[data-geographic-key]')
    .setAttribute('data-marker-hovered', true);
  marker.classList?.add('nhs-mq-marker-hovered--large');
  const { selected, viewed } = getClassesToApply({
    isAdult: marker.dataset.isAdult === 'true',
    isMasterPlannedCommunity:
      marker.dataset.isMasterPlannedCommunity === 'true',
    isSpotlight: marker.dataset.isSpotlight === 'true',
    isSelected: true,
    isViewed: true
  });
  selected?.forEach(className => marker.classList.add(className));
  viewed?.forEach(className => marker.classList.add(className));
  marker.dataset.selected = true;
  marker.dataset.hovered = true;
  const markerElement = marker?.closest('[data-marker-element]');
  markerElement?.classList?.add('active');
  markerElement?.classList?.add('marker--active');
};

/**
 * Opening in new tab the url of the marker.
 */
const openUrl = (marker, event) => {
  const interactionType =
    event.type === 'dblclick' ? 'double_click' : 'slow_double_click';

  // emit custom event with marker data
  const customEvent = new CustomEvent('openMarkerUrl', {
    detail: {
      marker,
      interactionType
    }
  });

  document.body.dispatchEvent(customEvent);

  const url = marker.url;
  window.open(url, '_blank');
};

/**
 * A function that is called when a marker is clicked.
 *
 * Used to emit a new action set the selectedMarker to the currently active marker object.
 */
const onMarkerClicked = (marker, event) => {
  if (event.domEvent.srcElement.hasAttribute('data-interactive-plan-button')) {
    return;
  }

  const googleAnalyticsDTO = marker.googleAnalyticsClickEvent;
  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));

  if (isMarkerActive(marker)) {
    openUrl(marker, event);
    removeMarkerHovered();
  }

  setActiveMarker(marker);
};

/**
 * hasExternalRepresentation function will calculate if the marker we are creating has a representation already in
 * the map, for instance: a site plan.
 * @param {Object} housingLocation the marker to be created.
 * @returns a flag indicating wheather the marker has a representation already in the map.
 */
const hasExternalRepresentation = housingLocation => {
  const state = store.getState();
  const sitePlans = selectSitePlans(state);
  return sitePlans.some(
    sitePlan => sitePlan.communityId === housingLocation.communityId
  );
};

/**
 * CreateMarker takes a housingLocation object and returns a new object with a markerPosition property
 * that has a lat and lng property.
 * @param housingLocation - the object that contains the Lat and Lng properties
 */
const configureMarker = housingLocation => ({
  events: {
    click: onMarkerClicked
  },
  googleAnalyticsClickEvent: housingLocation.getGoogleAnalyticsEventForClicks(),
  icon: housingLocation.getIcon(),
  id: housingLocation.id,
  markerPosition: {
    lat: +housingLocation.latitude,
    lng: +housingLocation.longitude
  },
  type: housingLocation.getType(),
  metaData: housingLocation.getHousingLocationMetaData(),
  zIndex: housingLocation.zIndex,
  url: housingLocation.getDetailUrl(),
  description: housingLocation.getDescription(),
  ignoreVisibilityUpdates: hasExternalRepresentation(housingLocation)
});

/**
 * Filters an array of marker objects to ensure only one marker per unique latitude/longitude pair is kept.
 * If multiple markers share the same coordinates, the marker with the highest zIndex is retained.
 *
 * @param {Array} markers - The array of marker objects to filter.
 * @returns {Array} A filtered array containing only the highest zIndex marker for each coordinate pair.
 */
const filterMarkersToPaint = markers => {
  const markersMap = new Map();

  markers.forEach(marker => {
    const key = `${marker.latitude},${marker.longitude}`;
    const existing = markersMap.get(key);

    if (!existing || marker.zIndex > existing.zIndex) {
      markersMap.set(key, marker);
    }
  });

  return Array.from(markersMap.values());
};

/**
 * It takes the current markers and the new markers, and returns an object with two arrays: one with
 * the markers to add, and one with the markers to remove.
 *
 * The setTimeout is to allow the browser to split the task improving the performance.
 */
const updateMarkers = markersFromStore => {
  const markers = filterMarkersToPaint(markersFromStore).map(configureMarker);
  setTimeout(() => paintMarker(markers));
  setTimeout(() => updateMarkersVisibility());
};

/**
 * requiresMarkerVisibilityChange determines if markers need to update their visibility, meaning: if we need to go
 * over ther markers to show/hide them based on the map viewport.
 * Function will return true if one of the following conditions happens:
 * * User has not changed map location, however the map changed its center, ie: user is dragging the map around.
 * * User is searching within map.
 * @param stateTransition - contains the output of a pairwise operation that brings previous and current state values.
 */
const requiresMarkerVisibilityChange = stateTransition => {
  const previousState = stateTransition[0];
  const currentState = stateTransition[1];
  return (
    (previousState.location === currentState.location &&
      previousState.coordinates !== currentState.coordinates) ||
    currentState.location.locationType ===
      LocationType.RectangularArea.toString()
  );
};

/**
 * Handles the selected marker by adding or removing the 'marker--active' class.
 * @param {Object} marker - The marker object.
 */
function handleSelectedMarker(marker) {
  const pastActive = document.querySelector('.marker--active');

  if (pastActive) {
    pastActive.classList.remove('marker--active');
  }

  if (marker) {
    const markers = document.querySelectorAll(
      `[data-id="${marker.id}"][data-type="${marker.type}"]`
    );

    markers.forEach(icon => {
      const container = icon?.closest('[data-marker-element]');
      container?.classList.add('marker--active');
    });
  }
}

/**
 * Handles a double click event on a map marker element.
 * @param {Event} event - The double click event object.
 */
function handleMarkerMapDoubleClick(event) {
  const state = store.getState()?.mapState;
  const marker = state?.selectedMarker;

  openUrl(marker, event);
  removeMarkerHovered();
}

/**
 * Ensures a hovered marker is created and displayed on the map for the given focused listing.
 *
 * - Checks if a marker DOM node exists for the focused listing (by id or communityId).
 * - If not, searches the markers from the store for a matching marker by id or communityId and type.
 * - If a matching marker is found, creates and displays a hovered marker for it.
 *
 * @param {Object} focusedListing - The listing object to focus/hover.
 */
const createHoveredMarkerOnDemand = focusedListing => {
  const { id, communityId } = focusedListing;
  const marker = getMarkerNode(focusedListing);

  if (!marker && id && communityId) {
    const markersFromStore = mapMarkers(store.getState());
    const listingMarker =
      markersFromStore.find(element => element.id === +id) ??
      markersFromStore.find(
        element =>
          element.communityId === +communityId &&
          element.type === MarkerType.BuildablePlan.toString()
      );

    if (listingMarker) {
      createHoveredMarker(configureMarker(listingMarker));
    }
  }
};

/* 
Subscribe to the store$ observable to listen for changes. When markers are available, map the results
property and compare if the value has changed from the previous execution. Then filters to see if the 
map boundaries are defined. 

This subscription handles the scenario when the results have changed.
 */
store$
  .pipe(
    filter(areMarkersAvailable),
    filter(areMapBoundariesDefined),
    map(mapMarkers),
    distinctUntilChanged()
  )
  .subscribe(markers => {
    const state = store.getState();
    const carousel = selectedNearbyListingCarousel(state);

    setTimeout(removeMarkers, 0);
    setTimeout(updateMarkers.bind(null, markers), 0);

    // only clear the summary card if available.
    if (carousel) {
      setActiveNearbyListingsCarousel(null);
    }
  });

/* 
Subscribe to the store$ observable to listen for changes. When markers the map boundaries are available, 
map the coordinates property and compare if the value has changed from the previous execution.
Then filters to see if there are results defined. 

This subscription handles the scenario when the map coordinates have changed.
 */
store$
  .pipe(
    filter(areMapBoundariesDefined),
    filter(state => !isFetchingResults(state)),
    filter(areMarkersAvailable),
    map(selectGeoCoordinatesAndLocation),
    distinctUntilKeyChanged('coordinates'),
    pairwise(),
    filter(requiresMarkerVisibilityChange)
  )
  .subscribe(updateMarkersVisibility);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in facts leaf change, fetch the new results for map.
 */
const facts$ = store$.pipe(map(factsSearchParameters), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in features leaf change, fetch the new results for map.
 */
const features$ = store$.pipe(
  map(featuresSearchParameters),
  distinctUntilChanged()
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in location leaf change, fetch the new results for map.
 */
const location$ = store$.pipe(
  filter(isNotCommunityLocationType),
  map(locationSearchParameters),
  distinctUntilChanged()
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in product leaf change, fetch the new results for map.
 */
const product$ = store$.pipe(
  map(productSearchParameters),
  distinctUntilChanged()
);

/**
 * If some stream has changed, fetch the results.
 * Don't remove the debounceTime because it helps to prevent duplicate the fetch result request if
 * some leaf change at the same time.
 */
combineLatest([facts$, features$, location$, product$])
  .pipe(debounceTime(100))
  .subscribe(fetchResults);

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the selectedMarker changes, map the selectedMarker and compare if the value has changed to valid value.
 * If that is the case run the clearActiveMarker function when the value is truly when an marker is active,
 * otherwise run setActiveMarker.
 */
const selectedMarker$ = store$.pipe(
  map(selectedMarker),
  distinctUntilChanged()
);

/**
 * Closes the marker card when the selectedMarker$ observable emits a value.
 * This is used to close any previously opened card.
 */
selectedMarker$.subscribe(closeMarkerCard);

/**
 * Subscribes to the `selectedMarker$` observable and opens a marker card when a marker with a valid ID is selected.
 * The marker icon element is extracted from the marker information and used to open the marker card.
 */
selectedMarker$
  .pipe(
    filter(marker => Boolean(marker?.metaData)),
    map(marker =>
      getMarkerNode({
        id: marker.metaData.id,
        communityId: marker.metaData.communityId
      })
    ),
    filter(Boolean)
  )
  .subscribe(openMarkerCard);

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the map coordinates have changed, map the coordinates and compare if the value has changed.
 * If that is the case run the handleMarkerMapChanges function.
 */
store$
  .pipe(map(geoCoordinates), distinctUntilChanged())
  .subscribe(handleMarkerMapChanges);

store$
  .pipe(map(selectedMarker), distinctUntilChanged())
  .subscribe(handleSelectedMarker);

/**
 * By subscribing to selectFocusedListing, we can effectively monitor changes in the Point of Interest listing.
 * This enables us to dynamically apply styles to the relevant marker, providing an intuitive visual indication
 * to the user regarding the location of a listing.
 */
store$
  .pipe(
    filter(mapMarkers),
    map(selectFocusedListingAndSitePlans),
    distinctUntilKeyChanged('focusedListing'),
    filter(
      ({ focusedListing, sitePlans }) =>
        !sitePlans.some(
          sitePlan => sitePlan.communityId === Number(focusedListing?.communityId)
        )
    ),
    tap(closeMarkerCard),
    tap(removeHoveredMarker),
    tap(({ focusedListing }) => createHoveredMarkerOnDemand(focusedListing)),
    debounceTime(100),
    map(({ focusedListing }) => getMarkerNode(focusedListing)),
    filter(Boolean)
  )
  .subscribe(openMarkerCard);

/**
 * An observable that emits when the map container is double-clicked, and the click target is an element with the
 * `data-marker-element` attribute.
 */
const markerDoubleClicked$ = mapContainerDoubleClicked$.pipe(
  filter(event => event.target.closest('[data-marker-element]'))
);

/**
 * Listens for a double click event on the map container element and subscribes to the handleMarkerMapDoubleClick
 * function. This allows the application to handle double click events on map markers and open the associated URL.
 */

markerDoubleClicked$.subscribe(handleMarkerMapDoubleClick);
/**
 * Listening for a click event on the map.
 */
mapContainer.addEventListener('mapHide', handleMarkerMapChanges);

mapContainerClicked$
  .pipe(
    filter(event => !isUserInteractingWithSitePlan(event)),
    filter(({ target }) => !target.closest('[data-marker-icon]'))
  )
  .subscribe(handleMarkerMapChanges);

  /*
 * Observable to raise the fetchPageNumber when the selected marker changed and:
 * 1. Isn't a small layout.
 * 2. The feature of AutoScroll is enable.
 * 3. The selected marker has page number.
 * 4. And the page of selected marker is different to the current listing page.
 */
store$
  .pipe(
    filter(
      state =>
        selectLayoutBreakpoint(state).getValue() !==
        LayoutBreakpoint.Small.getValue()
    ),
    filter(
      state => selectResultType(state) === ResultType.Communities.toString()
    ),
    filter(state => selectIsMarkerAutoScrollFeatureEnabled(state) === true),
    map(selectedMarkerAndPage),
    distinctUntilKeyChanged('marker'),
    filter(({ marker }) => marker?.pageNumber),
    filter(({ marker, page }) => marker.pageNumber !== page)
  )
  .subscribe(({ marker }) =>
    store.dispatch(searchParametersActions.fetchPageNumber(marker.pageNumber))
  );

/**
 * Listens for the 'markersUpdated' custom event on the document body and updates the content of affected markers.
 *
 * - When the 'markersUpdated' event is dispatched, receives the updated markers in event.detail.
 * - For each updated marker, reconfigures the marker (e.g., to get the latest icon HTML).
 * - Calls updateMarkerContent with the marker's id and new icon HTML string to update the marker's display on the map.
 *
 * This ensures that any changes to marker data (such as site plan updates or other dynamic changes)
 * are immediately reflected in the map UI without a full re-render.
 */
fromEvent(document.body, 'markersUpdated').subscribe(event => {
  const updatedMarkers = event.detail;
  if (Array.isArray(updatedMarkers)) {
    updatedMarkers.forEach(updatedMarker => {
      const { icon } = configureMarker(updatedMarker);
      requestAnimationFrame(() => updateMarkerContent(updatedMarker.id, icon));
    });
  }
});
